export type GateDisplayGroup = {
    key: string;
    label: string;
    prefixes: string[];
};

export const gateDisplayGroups: GateDisplayGroup[] = [
    { key: 'intent', label: 'Intent', prefixes: ['intent'] },
    { key: 'refine', label: 'Refine', prefixes: ['refine', 'refine_agent', 'hitl.refine', 'hitl.refine_agent', 'hitl_refine', 'hitl_refine_agent', 'refine_hitl'] },
    { key: 'planner', label: 'Planner', prefixes: ['planner', 'hitl.planner', 'hitl_planner', 'planner_hitl'] },
    { key: 'overall_narrative', label: 'Overall Narrative', prefixes: ['narrative', 'narrative_structured', 'overall_narrative'] },
    { key: 'execution', label: 'Execution', prefixes: ['execution'] },
    { key: 'sql', label: 'SQL', prefixes: ['sql', 'task_sql'] },
    { key: 'visualization', label: 'Visualization', prefixes: ['visualization', 'task_visualization'] },
    { key: 'task_narrative', label: 'Task Narrative', prefixes: ['task_narrative'] },
];

export function gateKeyMatchesPrefix(key: string, prefix: string): boolean {
    const normalizedKey = key.toLowerCase();
    const normalizedPrefix = prefix.toLowerCase();
    return normalizedKey === normalizedPrefix
        || normalizedKey.startsWith(`${normalizedPrefix}.`)
        || normalizedKey.startsWith(`${normalizedPrefix}_`);
}

export function targetAgentMatchesPrefixes(targetAgent: string | null | undefined, prefixes: string[]): boolean {
    if (!targetAgent) return true;
    const normalized = targetAgent.toLowerCase();
    const aliases: Record<string, string[]> = {
        narrative: ['narrative', 'narrative_structured', 'overall_narrative'],
        sql: ['sql', 'task_sql'],
        visualization: ['visualization', 'task_visualization'],
        task_narrative: ['task_narrative'],
        planner: ['planner', 'hitl.planner', 'hitl_planner', 'planner_hitl'],
        refine: ['refine', 'refine_agent', 'hitl.refine', 'hitl_refine'],
        execution: ['execution'],
        intent: ['intent'],
        retrieval: ['retrieval'],
    };
    const accepted = aliases[normalized] ?? [normalized];
    return prefixes.some((prefix) => accepted.some((alias) => gateKeyMatchesPrefix(prefix, alias) || gateKeyMatchesPrefix(alias, prefix)));
}

export function targetAgentMatchesGroup(targetAgent: string | null | undefined, group: GateDisplayGroup): boolean {
    if (!targetAgent) return true;
    if (targetAgent === 'narrative' && group.key === 'overall_narrative') return true;
    return targetAgentMatchesPrefixes(targetAgent, group.prefixes);
}

export function agentOrderIndex(agent: string): number {
    const normalized = agent.toLowerCase();
    const idx = gateDisplayGroups.findIndex((group) =>
        group.label.toLowerCase() === normalized
        || group.key.toLowerCase() === normalized
        || group.prefixes.some((prefix) => gateKeyMatchesPrefix(agent, prefix) || gateKeyMatchesPrefix(prefix, agent))
        || targetAgentMatchesPrefixes(agent, group.prefixes),
    );
    return idx >= 0 ? idx : Number.MAX_SAFE_INTEGER;
}

/** Pipeline order: Intent → Refine → Planner → Overall Narrative → Execution → SQL → Visualization → Task Narrative */
export function compareAgents(a: string, b: string): number {
    const orderDiff = agentOrderIndex(a) - agentOrderIndex(b);
    return orderDiff !== 0 ? orderDiff : a.localeCompare(b);
}
